/**
 * homelayout.ts — the deterministic furnished interior of a home + in-home routing, as PURE data
 * functions (no canvas, no browser deps) so they're unit-testable. A home is a stable, unique
 * function of its lotId: a HOME_TILES×HOME_TILES tile grid partitioned into rooms (bath/living +
 * kitchen/bedroom/workshop), each furnished and given doorways that are GUARANTEED to connect every
 * room (so pets can walk anywhere). scene.ts caches generateHomeLayout() per lot and renders it.
 */
export const HOME_TILES = 12;
export const MS_PER_TILE = 360;   // in-home walk pace (ms to cross one tile)

export interface Fixture { kind: string; tx: number; ty: number; tw: number; th: number; }
export interface Door { tx: number; ty: number; vert: boolean; }   // a 1-tile opening; vert = on a vertical (left/right) wall
export interface HomeRoom { name: string; tx: number; ty: number; tw: number; th: number; fixtures: Fixture[]; doors: Door[]; }
// a pet's in-home walk: tile-coord waypoints (fixture→doors→fixture) interpolated by arc length over a
// wall-clock window, so it glides between rooms through doorways. Presentation-only, rides the wall clock.
export interface HomeRoute { lotId: number; pts: { x: number; y: number }[]; cum: number[]; total: number; t0: number; dur: number; toRoom: string; toX: number; toY: number; }

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clampN = (v: number, lo: number, hi: number) => v < lo ? lo : v > hi ? hi : v;
// deterministic per-home RNG — homes are a stable, unique function of their lotId (no server/DB cost).
// also reused by the renderer for deterministic environmentals (clouds, lightning forks).
export function hash32(s: string): number { let h = 2166136261 >>> 0; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
export function mulb(seed: number): () => number { let a = seed >>> 0; return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** room index containing tile (tx,ty), or -1. */
export function roomAt(L: HomeRoom[], tx: number, ty: number): number {
    for (let i = 0; i < L.length; i++) { const R = L[i]!; if (tx >= R.tx && tx < R.tx + R.tw && ty >= R.ty && ty < R.ty + R.th) return i; }
    return -1;
}
/** the room on the OTHER side of door d (d belongs to room `self`), or -1. */
function doorOther(L: HomeRoom[], self: number, d: Door): number {
    const a = d.vert ? roomAt(L, d.tx - 1, d.ty) : roomAt(L, d.tx, d.ty - 1);
    const b = roomAt(L, d.tx, d.ty);
    return a === self ? b : a;
}
/** the passable world-tile point of a door (on the wall, mid-span). */
function doorPoint(d: Door): { x: number; y: number } { return d.vert ? { x: d.tx, y: d.ty + 0.5 } : { x: d.tx + 0.5, y: d.ty }; }
/** a door on the shared wall between A and B (mid-overlap), or null if not wall-adjacent. */
function sharedWallDoor(A: HomeRoom, B: HomeRoom): Door | null {
    if (A.tx + A.tw === B.tx) { const y0 = Math.max(A.ty, B.ty), y1 = Math.min(A.ty + A.th, B.ty + B.th); if (y1 > y0) return { tx: B.tx, ty: Math.floor((y0 + y1) / 2), vert: true }; }
    if (B.tx + B.tw === A.tx) { const y0 = Math.max(A.ty, B.ty), y1 = Math.min(A.ty + A.th, B.ty + B.th); if (y1 > y0) return { tx: A.tx, ty: Math.floor((y0 + y1) / 2), vert: true }; }
    if (A.ty + A.th === B.ty) { const x0 = Math.max(A.tx, B.tx), x1 = Math.min(A.tx + A.tw, B.tx + B.tw); if (x1 > x0) return { tx: Math.floor((x0 + x1) / 2), ty: B.ty, vert: false }; }
    if (B.ty + B.th === A.ty) { const x0 = Math.max(A.tx, B.tx), x1 = Math.min(A.tx + A.tw, B.tx + B.tw); if (x1 > x0) return { tx: Math.floor((x0 + x1) / 2), ty: A.ty, vert: false }; }
    return null;
}
/** ensure every room is door-reachable: union-find over existing doors, then carve doors on shared
 *  walls between disconnected rooms until one component remains. Pure geometry → deterministic. */
export function connectRooms(L: HomeRoom[]): void {
    const parent = L.map((_, i) => i);
    const find = (x: number): number => { while (parent[x] !== x) { parent[x] = parent[parent[x]!]!; x = parent[x]!; } return x; };
    const uni = (a: number, b: number) => { parent[find(a)] = find(b); };
    for (let i = 0; i < L.length; i++) for (const d of L[i]!.doors) { const o = doorOther(L, i, d); if (o >= 0) uni(i, o); }
    const comps = () => new Set(L.map((_, i) => find(i))).size;
    let guard = 0;
    while (comps() > 1 && guard++ < 64) {
        let added = false;
        for (let i = 0; i < L.length && !added; i++) for (let j = i + 1; j < L.length && !added; j++) {
            if (find(i) === find(j)) continue;
            const d = sharedWallDoor(L[i]!, L[j]!);
            if (d) { L[i]!.doors.push(d); uni(i, j); added = true; }
        }
        if (!added) break;
    }
}
/** room adjacency graph: each edge carries the door's passable point (tile coords). */
export function buildRoomGraph(L: HomeRoom[]): { to: number; px: number; py: number }[][] {
    const g: { to: number; px: number; py: number }[][] = L.map(() => []);
    for (let i = 0; i < L.length; i++) for (const d of L[i]!.doors) {
        const o = doorOther(L, i, d); if (o < 0) continue;
        const p = doorPoint(d); g[i]!.push({ to: o, px: p.x, py: p.y }); g[o]!.push({ to: i, px: p.x, py: p.y });
    }
    return g;
}
/** BFS the room graph for the door points to pass through from room `a` to room `b` (excl. endpoints). */
export function routeDoors(graph: { to: number; px: number; py: number }[][], a: number, b: number): { x: number; y: number }[] {
    if (a < 0 || b < 0 || a === b) return [];
    const prev = new Array<number>(graph.length).fill(-1), door: ({ x: number; y: number } | null)[] = new Array(graph.length).fill(null);
    const q = [a], seen = new Set([a]);
    while (q.length) { const u = q.shift()!; if (u === b) break; for (const e of graph[u]!) if (!seen.has(e.to)) { seen.add(e.to); prev[e.to] = u; door[e.to] = { x: e.px, y: e.py }; q.push(e.to); } }
    if (!seen.has(b)) return [];
    const out: { x: number; y: number }[] = [];
    for (let v = b; v !== a; v = prev[v]!) { const d = door[v]; if (d) out.unshift(d); }
    return out;
}
/** target tile (room-relative) for a pet at fixture `fixt` in pose `pose`: ON the fixture for
 *  lie/sit/soak, a step toward room centre for stand, room centre for floor / missing fixture. */
export function poseSpotTile(R: HomeRoom, fixt: string, pose: string): { x: number; y: number } {
    const f = fixt ? R.fixtures.find(ff => ff.kind === fixt) : undefined;
    if (!f) return { x: R.tx + R.tw / 2, y: R.ty + R.th / 2 };
    const fcx = f.tx + f.tw / 2, fcy = f.ty + f.th / 2;
    if (pose === 'stand') {
        const dx = (R.tx + R.tw / 2) - fcx, dy = (R.ty + R.th / 2) - fcy, len = Math.hypot(dx, dy) || 1;
        return { x: clampN(fcx + (dx / len) * 0.85, R.tx + 0.5, R.tx + R.tw - 0.5), y: clampN(fcy + (dy / len) * 0.85, R.ty + 0.5, R.ty + R.th - 0.5) };
    }
    return { x: fcx, y: fcy };   // lie / sit / soak → on the fixture
}
/** build a HomeRoute from tile waypoints (cumulative arc length + a duration scaled to length). */
export function makeHomeRoute(lotId: number, pts: { x: number; y: number }[], t0: number, toRoom: string, toX: number, toY: number): HomeRoute {
    const cum = [0]; let total = 0;
    for (let i = 1; i < pts.length; i++) { total += Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y); cum.push(total); }
    return { lotId, pts, cum, total, t0, dur: Math.max(1, Math.min(4000, total * MS_PER_TILE)), toRoom, toX, toY };
}
/** position along a HomeRoute at wall-clock `t` (arc-length interpolation; clamps at the end). */
export function routePos(rt: HomeRoute, t: number): { x: number; y: number } {
    if (rt.total < 1e-4 || rt.pts.length === 1) return rt.pts[rt.pts.length - 1]!;
    const a = clampN((t - rt.t0) / rt.dur, 0, 1), d = a * rt.total;
    let s = 1; while (s < rt.cum.length && rt.cum[s]! < d) s++;
    if (s >= rt.pts.length) return rt.pts[rt.pts.length - 1]!;
    const seg = rt.cum[s]! - rt.cum[s - 1]!, f = seg > 1e-6 ? (d - rt.cum[s - 1]!) / seg : 0;
    return { x: lerp(rt.pts[s - 1]!.x, rt.pts[s]!.x, f), y: lerp(rt.pts[s - 1]!.y, rt.pts[s]!.y, f) };
}
/** Furniture/appliances placed on the room's TILES (absolute grid coords), clamped to fit the room. */
function roomFixtures(name: string, R: { tx: number; ty: number; tw: number; th: number }): Fixture[] {
    const f: Fixture[] = [], w = R.tw, h = R.th;
    const put = (kind: string, ox: number, oy: number, tw: number, th: number) => {
        tw = Math.max(1, Math.min(tw, w)); th = Math.max(1, Math.min(th, h));
        f.push({ kind, tx: R.tx + Math.max(0, Math.min(w - tw, ox)), ty: R.ty + Math.max(0, Math.min(h - th, oy)), tw, th });
    };
    if (name === 'kitchen') {
        put('fridge', 0, 0, 1, 2); put('stove', 1, 0, 1, 1); put('counter', 2, 0, w - 2, 1); put('sink', Math.floor(w / 2), 0, 1, 1);
        if (w >= 4 && h >= 4) put('table', Math.floor(w / 2) - 1, Math.floor(h / 2), 2, 2);
    } else if (name === 'bedroom') {
        put('bed', 0, 0, Math.min(3, w - 1), Math.min(2, h - 1)); put('nightstand', Math.min(3, w - 1), 0, 1, 1);
        if (h >= 3) put('dresser', 0, h - 1, 2, 1); if (w >= 3 && h >= 3) put('rug', 1, h - 2, Math.min(3, w - 1), 1);
        put('lamp', w - 1, h - 1, 1, 1);
    } else if (name === 'bath') {
        put('tub', 0, 0, Math.min(3, w), 1); put('toilet', w - 1, h - 1, 1, 1); put('sink', 0, h - 1, 1, 1);
    } else if (name === 'living') {
        put('couch', 0, h - 1, Math.min(3, w), 1); put('tv', 1, 0, Math.min(3, w - 1), 1);
        if (w >= 3) put('coffee', 1, Math.floor(h / 2), 1, 1); put('shelf', w - 1, 0, 1, Math.min(3, h)); put('lamp', w - 1, h - 1, 1, 1);
    } else if (name === 'workshop') {
        put('bench', 0, h - 1, w, 1); put('pegboard', 0, 0, Math.min(4, w), 1); put('shelf', w - 1, 1, 1, Math.min(3, h - 1)); put('crate', w - 1, h - 1, 1, 1);
    } else {
        put('desk', 0, h - 1, Math.min(3, w), 1); put('shelf', w - 1, 0, 1, Math.min(3, h)); put('lamp', Math.floor(w / 2), Math.floor(h / 2), 1, 1);
        if (w >= 3 && h >= 3) put('rug', 1, 1, Math.min(2, w - 1), 1);
    }
    return f;
}
/** The deterministic furnished interior for a home: a 2-row guillotine partition into 5 rooms
 *  (bath = smallest, living = largest, kitchen/bedroom/workshop shuffled between), each furnished and
 *  doored, with connectivity guaranteed. Pure function of lotId — scene.ts caches the result. */
export function generateHomeLayout(lotId: number): HomeRoom[] {
    const rng = mulb(hash32('pphome:' + lotId)), N = HOME_TILES;
    const ySplit = 4 + Math.floor(rng() * 5);   // 4..8 tile rows
    const topN = rng() < 0.5 ? 2 : 3, botN = 5 - topN;
    const cells: { tx: number; ty: number; tw: number; th: number }[] = [];
    const row = (ty0: number, ty1: number, n: number) => {
        let rem = N; const ws: number[] = [];
        for (let i = 0; i < n; i++) { const last = i === n - 1, min = 3, mx = rem - min * (n - 1 - i); ws.push(last ? rem : min + Math.floor(rng() * Math.max(0, mx - min + 1))); rem -= ws[i]!; }
        let x = 0; for (const w of ws) { cells.push({ tx: x, ty: ty0, tw: w, th: ty1 - ty0 }); x += w; }
    };
    row(0, ySplit, topN); row(ySplit, N, botN);
    const byArea = cells.map((c, i) => ({ i, a: c.tw * c.th })).sort((p, q) => p.a - q.a);
    const names: string[] = new Array(cells.length).fill('study');
    names[byArea[0]!.i] = 'bath'; names[byArea[byArea.length - 1]!.i] = 'living';
    const mid = ['kitchen', 'bedroom', 'workshop'];
    for (let i = mid.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [mid[i], mid[j]] = [mid[j]!, mid[i]!]; }
    let mi = 0; for (let k = 1; k < byArea.length - 1; k++) names[byArea[k]!.i] = mid[mi++] ?? 'study';
    const L: HomeRoom[] = cells.map((c, i) => ({ name: names[i]!, tx: c.tx, ty: c.ty, tw: c.tw, th: c.th, fixtures: roomFixtures(names[i]!, c), doors: [] }));
    // each room gets one doorway on an interior wall, biased toward the home centre (so rooms connect)
    const cm = N / 2;
    for (const R of L) {
        const cand: Door[] = [];
        if (R.tx > 0) cand.push({ tx: R.tx, ty: R.ty + Math.floor(R.th / 2), vert: true });
        if (R.tx + R.tw < N) cand.push({ tx: R.tx + R.tw, ty: R.ty + Math.floor(R.th / 2), vert: true });
        if (R.ty > 0) cand.push({ tx: R.tx + Math.floor(R.tw / 2), ty: R.ty, vert: false });
        if (R.ty + R.th < N) cand.push({ tx: R.tx + Math.floor(R.tw / 2), ty: R.ty + R.th, vert: false });
        if (cand.length) { cand.sort((a, b) => (Math.abs(a.tx - cm) + Math.abs(a.ty - cm)) - (Math.abs(b.tx - cm) + Math.abs(b.ty - cm))); R.doors.push(cand[0]!); }
    }
    connectRooms(L);   // (#interiors) guarantee every room is door-reachable so pets can route anywhere
    return L;
}
